import LiveAgentsAvatarGroup from "@/components/call-center/live.agents.avatar.group";
import { useSidebar } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCallCenters } from "@/hooks/call-center/useCallCenters";
import { useLiveAgents } from "@/hooks/call-center/useLiveAgents";
import { useAppDispatch } from "@/hooks/redux-hooks";
import useAuthUser from "@/hooks/useAuthUser";
import { PATH_CALL_CENTER, PATH_CALL_CENTER_CLOUD_RECORDINGS, PATH_CALL_CENTER_COMPANY_ROOM, PATH_CALL_CENTER_EOD_REPORT } from "@/routes/paths";
import { getAllUsers } from "@/store/reducers/users.reducer";
import { Menu as MobileMenuIcon } from "lucide-react";
import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { getCcNavLinkClassName } from "./cc-nav-link.utils";
import { StatusDropdown } from "./status.dropdown";

export default function MainCallCenterTopHeader() {
  const dispatch = useAppDispatch();
  const { isMobile, toggleSidebar } = useSidebar();
  const { user, hasPermission } = useAuthUser();
  const canSubmitEodReport = (user?.assignedCallCenterIds?.length ?? 0) > 0;
  const canAccessRecordings = hasPermission("recordings:access");
  const { callCenters, selectedCallCenter, selectionReady, selectCallCenter } = useCallCenters();
  const isAssignedToSelectedCallCenter =
    !!selectedCallCenter && (user?.assignedCallCenterIds ?? []).includes(selectedCallCenter.id);
  const activeCallCenters = callCenters.filter((cc) => cc.isCallCenterActive);
  const { total, liveAgents, loading } = useLiveAgents(selectionReady ? selectedCallCenter?.id : undefined);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  return (
    <header className="flex flex-col w-full p-4 md:px-6">
      <div className="flex flex-col md:flex-row w-full items-center gap-4 md:justify-between border-b border-dashboard-border pb-4">
        <div className="flex justify-between items-center w-full md:w-auto">
          <div className="flex gap-3 items-center">
            <img src="/logos/ironpipeline-mobile.png" className="w-10 md:hidden" alt="IronPipeline" />
            <div className="text-md lg:text-xl xl:text-2xl whitespace-nowrap">Call Center</div>
            {activeCallCenters.length > 0 && (
              <Select
                value={selectedCallCenter ? String(selectedCallCenter.id) : ""}
                onValueChange={(val) => {
                  const cc = activeCallCenters.find((c) => String(c.id) === val) ?? null;
                  selectCallCenter(cc);
                }}
              >
                <SelectTrigger className="h-8 w-auto min-w-[80px] text-sm border-gray-600 bg-transparent">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {activeCallCenters.map((cc) => (
                    <SelectItem key={cc.id} value={String(cc.id)}>
                      {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {isMobile && <MobileMenuIcon className="cursor-pointer" onClick={toggleSidebar} />}
        </div>

        <div className="flex flex-col md:flex-row-reverse items-center gap-3 lg:gap-1 xl:gap-4 w-full md:w-auto">
          <div className="flex items-center gap-2 w-full">
            {isAssignedToSelectedCallCenter && <StatusDropdown />}
            <LiveAgentsAvatarGroup total={total} liveAgents={liveAgents} loading={loading} />
          </div>

          <div className="flex gap-1 lg:gap-3 xl:gap-8 w-full md:w-auto justify-between text-[#97AAC4] flex-nowrap whitespace-nowrap">
            <NavLink to={PATH_CALL_CENTER} end className={getCcNavLinkClassName}>
              Dashboard
            </NavLink>
            <NavLink to={PATH_CALL_CENTER_COMPANY_ROOM} className={getCcNavLinkClassName}>
              Company Room
            </NavLink>
            {canAccessRecordings && (
              <NavLink to={PATH_CALL_CENTER_CLOUD_RECORDINGS} className={getCcNavLinkClassName}>
                Recordings
              </NavLink>
            )}
            {canSubmitEodReport && (
              <NavLink to={PATH_CALL_CENTER_EOD_REPORT} className={getCcNavLinkClassName}>
                EOD Report
              </NavLink>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
